import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Alert, Space, Typography, Spin } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { efirmaFiscalService } from '../../services/api';

const { Text } = Typography;

const CONSENTIMIENTO = {
  sin_solicitar: { color: 'default', label: 'Sin solicitar' },
  solicitado: { color: 'orange', label: 'Solicitado, sin responder' },
  otorgado: { color: 'green', label: 'Otorgado' },
  revocado: { color: 'red', label: 'Revocado' },
};

/**
 * Estado de la e.firma de un contribuyente de la cartera.
 *
 * No muestra ni permite descargar material: solo si está cargada, si el
 * contribuyente autorizó y, sobre todo, si se puede exportar. Ese último dato
 * es el que el operador necesita antes de intentar nada con Orquestax.
 */
const EfirmaPanel = ({ empresa }) => {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  const cargar = async () => {
    if (!empresa?.id) return;
    setCargando(true);
    setError('');

    try {
      setEstado(await efirmaFiscalService.getCompanyStatus(empresa.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar el estado de la e.firma');
    } finally {
      setCargando(false);
    }
  };

  if (cargando) {
    return <Card title="e.firma"><Spin /> <Text type="secondary">Cargando…</Text></Card>;
  }

  if (error) {
    return <Card title="e.firma"><Alert type="error" message={error} showIcon /></Card>;
  }

  const consent = CONSENTIMIENTO[estado?.consentimiento?.estado] || CONSENTIMIENTO.sin_solicitar;

  return (
    <Card title={<span><SafetyCertificateOutlined /> e.firma</span>}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Lo primero es si se puede exportar: es la pregunta que trae al
            operador a esta tarjeta. */}
        {estado?.exportable ? (
          <Alert
            type="success"
            showIcon
            message="Disponible para exportar"
            description="El contribuyente autorizó y su e.firma está activa."
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No disponible para exportar"
            description={
              !estado?.efirma?.cargada
                ? 'Este contribuyente no tiene e.firma cargada.'
                : estado?.consentimiento?.estado === 'solicitado'
                  ? 'Se le solicitó la autorización por correo y todavía no responde.'
                  : estado?.consentimiento?.estado === 'revocado'
                    ? 'El contribuyente retiró su autorización.'
                    : estado?.consentimiento?.estado === 'sin_solicitar'
                      ? 'Falta solicitar su autorización.'
                      : 'Su e.firma fue eliminada y permanece resguardada sin poder usarse.'
            }
          />
        )}

        <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
          <Descriptions.Item label="e.firma cargada">
            {estado?.efirma?.cargada
              ? <Tag color={estado.efirma.activa ? 'green' : 'orange'}>{estado.efirma.activa ? 'Sí, activa' : 'Sí, inactiva'}</Tag>
              : <Tag>No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Consentimiento">
            <Tag color={consent.color}>{consent.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Última carga">
            {estado?.efirma?.ultima_carga || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Autorizado el">
            {estado?.consentimiento?.otorgado_en || '—'}
          </Descriptions.Item>
        </Descriptions>

        <Text type="secondary" style={{ fontSize: 12 }}>
          El material de la e.firma nunca se muestra aquí. La exportación se hace desde
          Orquestax, hacia un dispositivo autorizado, y queda registrada en la bitácora.
        </Text>
      </Space>
    </Card>
  );
};

export default EfirmaPanel;
